// Practice schedule for /practices (and the home "Schedule" button).
// Edited in Connect → Admin → Website; anon can read active rows only.

import { supabase } from "@/lib/supabase";
import type { PracticeData, PracticeNotice, PracticeSession, SpecialSession } from "@/mocks/practices";

const DAY_NAMES = ["", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** "17:30:00" → "17:30" */
const hhmm = (t: string) => t.slice(0, 5);

/** Now in Dallas as "YYYY-MM-DD HH:MM". */
function nowInDallas(): string {
  return new Date()
    .toLocaleString("sv-SE", { timeZone: "America/Chicago", hour12: false })
    .slice(0, 16);
}

/** Special sessions still showing: they hide at 11 PM Central on their "visible until" day
 *  (same rule as the practice_special_public_read policy). */
export function liveSpecials(specials: SpecialSession[]): SpecialSession[] {
  const now = nowInDallas();
  return specials.filter((s) => now < `${s.visibleUntil} 23:00`);
}

/** Notices still showing (same 11 PM Central rule as special sessions). */
export function liveNotices(notices: PracticeNotice[]): PracticeNotice[] {
  const now = nowInDallas();
  return notices.filter((n) => !n.visibleUntil || now < `${n.visibleUntil} 23:00`);
}

export async function getPracticeData(): Promise<PracticeData> {
  const { data: season, error: seasonErr } = await supabase
    .from("seasons")
    .select("name")
    .eq("is_active", true)
    .limit(1)
    .single();
  if (seasonErr || !season) throw seasonErr ?? new Error("No active season");

  const [locRes, sesRes, spRes, noRes] = await Promise.all([
    supabase.from("practice_locations").select("id, acronym, name, address, sort_order").order("sort_order"),
    supabase
      .from("practice_sessions")
      .select("day_of_week, start_time, end_time, location_id, grade_min, grade_max, label, is_paid, is_open_to_all, starts_on, sort_order")
      .eq("season", season.name)
      .order("day_of_week")
      .order("start_time")
      .order("sort_order"),
    supabase
      .from("practice_special_sessions")
      .select("block_title, event_date, start_time, end_time, group_label, location_id, visible_until")
      .order("event_date")
      .order("start_time"),
    supabase
      .from("practice_notices")
      .select("title, message, visible_until")
      .order("created_at", { ascending: false }),
  ]);
  if (locRes.error) throw locRes.error;
  if (sesRes.error) throw sesRes.error;
  if (spRes.error) throw spRes.error;

  // Inactive locations are not returned to anon, so a session there shows no location.
  const acronymById = new Map((locRes.data ?? []).map((l) => [l.id, l.acronym as string]));

  const sessions: PracticeSession[] = (sesRes.data ?? []).map((r) => ({
    day: DAY_NAMES[r.day_of_week] ?? "",
    start24: hhmm(r.start_time),
    end24: hhmm(r.end_time),
    location: acronymById.get(r.location_id) ?? "",
    gradeMin: r.grade_min,
    gradeMax: r.grade_max,
    universal: r.is_open_to_all,
    paid: r.is_paid,
    label: r.label,
    startsOn: r.starts_on,
  }));

  const specials: SpecialSession[] = (spRes.data ?? []).map((r) => ({
    blockTitle: r.block_title,
    date: r.event_date,
    start24: hhmm(r.start_time),
    end24: hhmm(r.end_time),
    group: r.group_label,
    location: r.location_id ? acronymById.get(r.location_id) ?? null : null,
    visibleUntil: r.visible_until,
  }));

  // A notice problem must not take the whole schedule down to the fallback.
  if (noRes.error) console.warn("[practices] notices:", noRes.error.message);
  const notices: PracticeNotice[] = (noRes.data ?? []).map((r) => ({
    title: r.title,
    message: r.message,
    visibleUntil: r.visible_until,
  }));

  // Only list locations the schedule actually uses.
  const used = new Set([...sessions.map((s) => s.location), ...specials.map((s) => s.location)]);
  const locations = (locRes.data ?? [])
    .filter((l) => used.has(l.acronym))
    .map((l) => ({ acronym: l.acronym, name: l.name, address: l.address ?? "" }));

  return { season: season.name, sessions, specials, notices, locations };
}
